import { mat4, vec3, quat } from 'gl-matrix';
import { Scene } from '../scene/Scene';
import { SceneFeature } from '../scene/feature';
import { PhysicsBody } from './PhysicsBody';
import { Projectile, ProjectileOptions } from './Projectile';
import { quatToEulerDegrees } from './quatUtil';
import { selectBestSettleFace, buildSettleQuaternion } from './settleTarget';
import { VolumeGridLike } from '../navigation-mesh';
import { BoneHitDetection } from './BoneHitDetection';
import { BoneHitResult } from './BoneCollisionBody';
import { BoneImpulseSystem } from './BoneImpulse';
import { MeleeAttackSystem } from './MeleeAttackSystem';
import { GltfAttachmentHolder } from '../object/attachments/GltfAttachmentHolder';

// Cap the integration step at 50ms (20 Hz) so a stalled tab / GC pause doesn't
// produce a single huge dt that tunnels projectiles through walls and floors.
const MAX_PHYSICS_TIMESTEP_SECONDS = 0.05;

export class PhysicsFeature implements SceneFeature {
  private bodies = new Set<PhysicsBody>();
  private projectiles = new Set<Projectile>();
  private scene!: Scene;
  public boneHitDetection = new BoneHitDetection();
  public boneImpulseSystem = new BoneImpulseSystem();
  public meleeAttackSystem = new MeleeAttackSystem(this.boneHitDetection, this.boneImpulseSystem);

  init(scene: Scene): void {
    this.scene = scene;
  }

  public addBody(body: PhysicsBody): void {
    this.bodies.add(body);
  }

  public removeBody(body: PhysicsBody): void {
    this.bodies.delete(body);
  }

  public createProjectile(options: ProjectileOptions): Projectile {
    let proj = new Projectile(options);
    this.projectiles.add(proj);
    this.bodies.add(proj.body);
    if (proj.renderObject) {
      this.scene.objectManager.addObject(proj.renderObject);
    }
    return proj;
  }

  public registerProjectile(options: ProjectileOptions): Projectile {
    let proj = new Projectile(options);
    this.projectiles.add(proj);
    this.bodies.add(proj.body);
    return proj;
  }

  public tick(deltaTime: number): void {
    let dt = Math.min(deltaTime, MAX_PHYSICS_TIMESTEP_SECONDS);

    for (let body of this.bodies) {
      // For bodies not yet grounded and without a consumer-set surfaceHeight,
      // query the volume grid to find the floor
      if (!body.grounded && body.surfaceHeight === null) {
        body.surfaceHeight = this.querySurfaceHeight(body.position);
      }
      body.integrate(dt);
    }

    // Bone hit detection — test projectiles against animated models
    // before VolumeGrid collision so bone hits take priority
    const boneHits = this.boneHitDetection.testProjectiles(this.projectiles);
    for (let [proj, hit] of boneHits) {
      this.handleBoneHit(proj, hit);
    }

    for (let proj of this.projectiles) {
      proj.lifetime += dt;
      if (proj.boneHitCooldown > 0) proj.boneHitCooldown -= dt;

      if (proj.lifetime >= proj.maxLifetime) {
        proj.alive = false;
      }

      // Skip projectiles already killed by bone hit
      if (boneHits.has(proj) && !proj.alive) {
        // Still sync position for stuck projectiles
        if (proj.renderObject && proj.persist) {
          vec3.copy(proj.renderObject.position as vec3, proj.body.position as vec3);
        }
      } else if (proj.alive && proj.settled) {
        this.updateSettledProjectile(proj, dt);
      } else if (proj.alive) {
        this.updateProjectileCollision(proj);
      }

      if (proj.alive && proj.renderObject) {
        vec3.copy(proj.renderObject.position as vec3, proj.body.position as vec3);

        // Sync orientation to render object
        if (proj.alignToVelocity) {
          let euler = this.velocityToEulerDegrees(proj.body.velocity, proj.forwardRollOffset);
          if (euler !== null) {
            proj.renderObject.trs.setRotation(euler[0], euler[1], euler[2]);
          }
        } else if (proj.body.inverseInertiaDiag !== null) {
          let euler = quatToEulerDegrees(proj.body.orientation);
          proj.renderObject.trs.setRotation(euler[0], euler[1], euler[2]);
        }
      }

      if (!proj.alive) {
        this.destroyProjectile(proj);
      }
    }

    // Resolve scheduled melee attacks (fires bone impulses on hit)
    this.meleeAttackSystem.tick(dt);

    // Decay bone impulses (visual hit reactions)
    this.boneImpulseSystem.tick(dt);
  }

  private updateProjectileCollision(proj: Projectile): void {
    let body = proj.body;
    let grid = this.scene.volumeGrid;
    if (!grid) return;

    let x = body.position[0];
    let y = body.position[1];
    let z = body.position[2];
    let prev = body.previousPosition;

    // Floor collision — check first so floor crossings aren't mishandled as walls.
    // getFloorHeightBelow always returns the floor for flat leaves (no tolerance guard),
    // so tunneling through flat terrain is reliably caught.
    // For complex leaves at terrain edges, fall back to previous position.
    let floorHeight = grid.getFloorHeightBelow(x, y, z);
    if (floorHeight === null) {
      let prevFloor = grid.getFloorHeightBelow(prev[0], prev[1], prev[2]);
      if (prevFloor !== null && y < prevFloor) {
        floorHeight = prevFloor;
      }
    }

    if (floorHeight !== null) {
      let clearance =
        body.inverseInertiaDiag !== null ? body.getGroundClearance() : body.surfaceOffset;
      let restHeight = floorHeight + clearance;

      body.surfaceHeight = floorHeight;

      // Ceiling collision
      let ceilingHeight = grid.getCeilingHeightAbove(x, y, z);
      if (ceilingHeight === null) {
        let prevCeiling = grid.getCeilingHeightAbove(prev[0], prev[1], prev[2]);
        if (prevCeiling !== null && y > prevCeiling) {
          ceilingHeight = prevCeiling;
        }
      }
      if (ceilingHeight !== null) {
        let topClearance =
          body.inverseInertiaDiag !== null ? body.getCeilingClearance() : body.surfaceOffset;
        let ceilingLimit = ceilingHeight - topClearance;

        if (y >= ceilingLimit) {
          if (proj.stickOnImpact) {
            this.stickProjectile(proj);
            return;
          }
          body.position[1] = ceilingLimit;
          if (body.velocity[1] > 0) {
            let preVelocity = vec3.clone(body.velocity);
            body.velocity[1] = -body.velocity[1] * body.restitution;
            this.applyCollisionAngularImpulse(body, preVelocity);
          }
        }
      }

      if (y <= restHeight) {
        if (proj.stickOnImpact) {
          this.stickProjectile(proj);
          return;
        }
        body.position[1] = restHeight;
        proj.bounceCount++;

        let sample = grid.getFloorHeightWithNormal(x, restHeight, z);
        if (proj.onHit) {
          proj.onHit(body.position as vec3, sample ? sample.normal : vec3.fromValues(0, 1, 0));
        }

        let speed = vec3.length(body.velocity);
        if (proj.bounceCount >= proj.maxBounces || speed < 0.5) {
          vec3.zero(body.velocity);
          this.bodies.delete(body);
          proj.settled = true;
          proj.settleStart = quat.clone(body.orientation);
          proj.settleTarget = this.computeSettleTarget(body);
          proj.settleProgress = 0;
          return;
        }

        let preVelocity = vec3.clone(body.velocity);

        let normal = sample ? sample.normal : vec3.fromValues(0, 1, 0);
        reflectVelocity(body.velocity, normal, body.restitution);

        this.applyCollisionAngularImpulse(body, preVelocity);
        return;
      }
    }

    // Wall collision — only reached if no floor/ceiling collision was handled
    if (grid.rayCrossesSolid(prev[0], prev[1], prev[2], x, y, z)) {
      this.handleWallBounce(proj, grid);
    }
  }

  private updateSettledProjectile(proj: Projectile, dt: number): void {
    let body = proj.body;

    // Simple slerp to target — the euler extraction fix means the rendered
    // orientation now matches the quaternion correctly, so no yaw drift.
    proj.settleProgress = Math.min(proj.settleProgress + dt * 3.0, 1);
    let t = proj.settleProgress;
    let ease = t * t * (3 - 2 * t); // smoothstep

    quat.slerp(body.orientation, proj.settleStart!, proj.settleTarget!, ease);

    // Pin lowest point to surface
    let clearance = body.getGroundClearance();
    let grid = this.scene.volumeGrid;
    if (grid) {
      let surfaceY = grid.getFloorHeight(body.position[0], body.position[1], body.position[2]);
      if (surfaceY !== null) {
        body.position[1] = surfaceY + clearance;
      }
    }

    if (proj.settleProgress >= 1) {
      vec3.zero(body.angularVelocity);
      proj.alive = false;
    }
  }

  private handleBoneHit(proj: Projectile, hit: BoneHitResult): void {
    let body = proj.body;
    let collisionBody = hit.target;
    let targetObj = collisionBody.renderObject;

    // Fire bone-hit callback
    if (proj.onBoneHit) {
      proj.onBoneHit(hit);
    }

    // Fire generic hit callback
    if (proj.onHit) {
      let hitNormal = vec3.sub(vec3.create(), hit.hitPoint, targetObj.trs.position as vec3);
      vec3.normalize(hitNormal, hitNormal);
      proj.onHit(hit.hitPoint, hitNormal);
    }

    // Apply bone impulse — magnitude from momentum (mass × speed)
    let dir = vec3.normalize(vec3.create(), body.velocity);
    let speed = vec3.length(body.velocity);
    let momentum = body.mass * speed;
    let magnitude = Math.min(0.05 + momentum * 0.008, 0.6);
    this.boneImpulseSystem.applyImpulse(targetObj, hit.boneCapsule.jointIndex, dir, magnitude);

    if (proj.stickOnImpact && proj.renderObject) {
      // Snap to the actual capsule entry point. body.position has overshot the
      // bone because integration ran before hit detection — for fast arrows vs
      // small bone capsules, that overshoot is enough to put the arrow entirely
      // through the model. Rewind body.position too so any later reads (and the
      // bone-local offset computed below from renderObject.modelMatrix) are
      // consistent with the snapped render position.
      vec3.copy(body.position as vec3, hit.hitPoint);
      vec3.copy(proj.renderObject.position as vec3, hit.hitPoint);
      if (proj.alignToVelocity) {
        let euler = this.velocityToEulerDegrees(body.velocity, proj.forwardRollOffset);
        if (euler) {
          proj.renderObject.trs.setRotation(euler[0], euler[1], euler[2]);
        }
      }

      // Position the arrow so its TIP lands at hit.hitPoint. Arrow models have
      // their pivot at the back of the shaft (see Projectile.forwardRollOffset
      // detection), so without backing the pivot up by the projectile length the
      // entire shaft would extend forward through the bone.
      // penetrationFraction now means "fraction of shaft embedded past the
      // surface" — 0 = tip flush, 0.2 = 20% of length sunk into the bone.
      let speed = vec3.length(body.velocity);
      if (speed > 0.01) {
        let velDir = vec3.normalize(vec3.create(), body.velocity);
        let length = this.getProjectileLength(proj);
        let offset = length * (proj.penetrationFraction - 1);
        vec3.scaleAndAdd(
          proj.renderObject.position as vec3,
          proj.renderObject.position as vec3,
          velDir,
          offset,
        );
      }

      // Attach projectile to the hit bone
      if (!targetObj.attachments) {
        targetObj.attachments = new GltfAttachmentHolder(targetObj);
      }

      let attachment = targetObj.attachments.setAttachment(
        hit.boneCapsule.jointName,
        proj.renderObject,
      );

      // Compute offset: inverse(boneWorldMatrix) * arrowWorldMatrix.
      // preRenderAttachments decomposes the joint matrix into pos+rot (no scale),
      // so the bone world matrix here must match that: use fromRotationTranslation.
      let frameSet = targetObj.animationState!.getFrames();
      let frame = frameSet.frames[hit.boneCapsule.jointName];
      if (frame) {
        let jm = frame.jointMatrix;
        let bonePos = mat4.getTranslation(vec3.create(), jm);
        let boneRot = mat4.getRotation(quat.create(), jm);
        let jointClean = mat4.fromRotationTranslation(mat4.create(), boneRot, bonePos);
        let boneWorld = mat4.multiply(mat4.create(), targetObj.modelMatrix, jointClean);
        let inverseBoneWorld = mat4.invert(mat4.create(), boneWorld);
        if (inverseBoneWorld) {
          mat4.multiply(attachment.offsetMatrix, inverseBoneWorld, proj.renderObject.modelMatrix);
        }
      }

      this.killProjectile(proj);
    } else {
      // Non-stick projectile: bounce off the capsule surface
      let { a, b } = collisionBody.getWorldCapsuleEndpoints(
        hit.boneCapsule,
        targetObj.animationState!.getFrames(),
        targetObj.modelMatrix,
        targetObj.boneOverrides,
      );
      let boneCenter = closestPointOnSegment(hit.hitPoint, a, b);
      let hitNormal = vec3.sub(vec3.create(), hit.hitPoint, boneCenter);
      vec3.normalize(hitNormal, hitNormal);
      reflectVelocity(body.velocity, hitNormal, body.restitution);

      // Push projectile outside the capsule to prevent re-collision
      let capsuleRadius = hit.boneCapsule.radius * targetObj.trs.scale;
      vec3.scaleAndAdd(body.position as vec3, boneCenter, hitNormal, capsuleRadius + 0.1);

      // Cooldown prevents repeated bone hits while exiting the model
      proj.boneHitCooldown = 0.3;

      proj.bounceCount++;
      if (proj.bounceCount >= proj.maxBounces) {
        proj.alive = false;
      }
    }
  }

  private computeSettleTarget(body: PhysicsBody): quat {
    let face = selectBestSettleFace(
      body.orientation,
      body.centerOfMassOffset,
      body.localMin,
      body.localMax,
    );
    return buildSettleQuaternion(
      face.normal,
      face.axisIndex,
      body.orientation,
      body.localMin,
      body.localMax,
    );
  }

  private applyCollisionAngularImpulse(body: PhysicsBody, preVelocity: vec3): void {
    if (body.inverseInertiaDiag === null) return;

    // r = vector from center of mass to contact point (world space)
    // Contact is at the body position (ground). CoM is offset from mesh origin.
    let rotatedOffset = vec3.transformQuat(
      vec3.create(),
      body.centerOfMassOffset,
      body.orientation,
    );
    // Contact point relative to CoM: negate the offset (contact is below CoM)
    let r = vec3.negate(vec3.create(), rotatedOffset);

    // Velocity change from collision
    let dv = vec3.sub(vec3.create(), body.velocity, preVelocity);

    // Angular impulse = r x dv (mass cancels with inertia tensor which includes mass)
    // Scale down to avoid excessive spin — in practice objects lose energy to
    // deformation, friction at the contact patch, etc.
    vec3.scale(dv, dv, 0.3);
    let torque = vec3.cross(vec3.create(), r, dv);

    // Transform torque to body-local frame
    let invOrientation = quat.invert(quat.create(), body.orientation);
    let localTorque = vec3.transformQuat(vec3.create(), torque, invOrientation);

    // deltaOmega = I_inv * torque
    let deltaOmega = vec3.fromValues(
      localTorque[0] * body.inverseInertiaDiag[0],
      localTorque[1] * body.inverseInertiaDiag[1],
      localTorque[2] * body.inverseInertiaDiag[2],
    );

    // Transform back to world space and add to angular velocity
    let worldDeltaOmega = vec3.transformQuat(vec3.create(), deltaOmega, body.orientation);
    vec3.add(body.angularVelocity, body.angularVelocity, worldDeltaOmega);
  }

  private handleWallBounce(proj: Projectile, grid: VolumeGridLike): void {
    if (proj.stickOnImpact) {
      this.stickProjectile(proj);
      return;
    }
    let body = proj.body;
    let prev = body.previousPosition;
    let px = prev[0];
    let py = prev[1];
    let pz = prev[2];

    // Reset to previous position (before tunneling through the wall)
    body.position[0] = px;
    body.position[1] = py;
    body.position[2] = pz;

    // Save pre-reflection velocity for angular impulse
    let preVelocity = vec3.clone(body.velocity);

    // Derive wall normal by probing which axes are blocked
    let vs = grid.voxelSize;
    let solidInX = grid.isSolidAt(px + vs * Math.sign(body.velocity[0]), py, pz);
    let solidInZ = grid.isSolidAt(px, py, pz + vs * Math.sign(body.velocity[2]));

    let wallNormal = vec3.create();
    if (solidInX) {
      wallNormal[0] = -Math.sign(body.velocity[0]);
    }
    if (solidInZ) {
      wallNormal[2] = -Math.sign(body.velocity[2]);
    }
    if (!solidInX && !solidInZ) {
      // Diagonal — both axes blocked
      wallNormal[0] = -Math.sign(body.velocity[0]);
      wallNormal[2] = -Math.sign(body.velocity[2]);
    }
    vec3.normalize(wallNormal, wallNormal);

    reflectVelocity(body.velocity, wallNormal, body.restitution);

    // Apply angular impulse from wall bounce
    this.applyCollisionAngularImpulse(body, preVelocity);

    proj.bounceCount++;
    if (proj.bounceCount >= proj.maxBounces) {
      proj.alive = false;
    }
  }

  private querySurfaceHeight(position: vec3 | number[]): number | null {
    let grid = this.scene.volumeGrid;
    if (!grid) return null;
    return grid.getFloorHeight(position[0], position[1], position[2]);
  }

  private stickProjectile(proj: Projectile): void {
    let body = proj.body;

    // Offset position along velocity direction for penetration effect
    let speed = vec3.length(body.velocity);
    if (speed > 0.01 && proj.renderObject) {
      let dir = vec3.normalize(vec3.create(), body.velocity);
      let penetration = this.getProjectileLength(proj) * proj.penetrationFraction;
      vec3.scaleAndAdd(body.position as vec3, body.position as vec3, dir, penetration);
    }

    this.killProjectile(proj);

    if (proj.onHit) {
      proj.onHit(body.position as vec3, vec3.fromValues(0, 1, 0));
    }
  }

  /** Longest dimension of the projectile mesh in world units. */
  private getProjectileLength(proj: Projectile): number {
    if (!proj.renderObject) return 0;
    let bounds = proj.renderObject.bounds;
    let extents = vec3.sub(vec3.create(), bounds.localMax, bounds.localMin);
    return Math.max(extents[0], extents[1], extents[2]) * proj.renderObject.trs.scale;
  }

  /** Stop, detach from physics, and mark as persistent so it stays in the scene. */
  private killProjectile(proj: Projectile): void {
    let body = proj.body;
    vec3.zero(body.velocity);
    vec3.zero(body.angularVelocity);
    this.bodies.delete(body);
    proj.persist = true;
    proj.alive = false;
  }

  private velocityToEulerDegrees(
    velocity: vec3,
    rollOffset: number,
  ): [number, number, number] | null {
    let speed = vec3.length(velocity);
    if (speed < 0.01) return null;

    let vx = velocity[0] / speed;
    let vy = velocity[1] / speed;
    let vz = velocity[2] / speed;

    // TRS applies Rx * Ry * Rz to points (Rz first, then Ry, then Rx).
    // Rz(roll) rotates model forward into +X (for +Y models) or keeps +Z.
    // Ry(yaw) aims in XZ plane. Rx(pitch) tilts up/down.
    //
    // For +Z forward (rollOffset=0): yaw=atan2(vx,vz), pitch from vy.
    // For +Y forward (rollOffset=-90): Rz maps +Y→+X, then Ry aims +X.
    //   Ry(θ) on +X: x'=cos(θ), z'=-sin(θ). We want x'=vxn, z'=vzn
    //   so yaw = atan2(-vz, vx).

    let yaw: number;
    if (Math.abs(rollOffset) > 45) {
      // +Y forward: after Rz(-90), forward is +X. Ry(yaw) on (1,0,0):
      //   x=cos(yaw), z=-sin(yaw). Want (vx/h, 0, vz/h) so yaw=atan2(-vz,vx).
      yaw = Math.atan2(-vz, vx) * (180 / Math.PI);
    } else {
      // +Z forward: Ry(yaw) on (0,0,1): x=sin(yaw), z=cos(yaw).
      yaw = Math.atan2(vx, vz) * (180 / Math.PI);
    }
    let pitch = -Math.asin(vy) * (180 / Math.PI);

    return [pitch, yaw, rollOffset];
  }

  private destroyProjectile(proj: Projectile): void {
    this.projectiles.delete(proj);
    this.bodies.delete(proj.body);
    if (proj.renderObject && !proj.persist) {
      this.scene.objectManager.removeObject(proj.renderObject);
    }
  }
}

/**
 * Reflect velocity against a surface normal with restitution:
 * v' = (v - 2(v·n)n) * restitution
 */
/** Closest point on line segment AB to point P. */
function closestPointOnSegment(p: vec3, a: vec3, b: vec3): vec3 {
  const ab = vec3.sub(vec3.create(), b, a);
  const abLenSq = vec3.dot(ab, ab);
  if (abLenSq < 0.0001) return vec3.clone(a);
  let t = vec3.dot(vec3.sub(vec3.create(), p, a), ab) / abLenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  return vec3.scaleAndAdd(vec3.create(), a, ab, t);
}

function reflectVelocity(velocity: vec3, normal: vec3, restitution: number): void {
  let dot = vec3.dot(velocity, normal);
  let reflection = vec3.scaleAndAdd(vec3.create(), velocity, normal, -2 * dot);
  vec3.scale(velocity, reflection, restitution);
}
